import { useState } from "react";
import { isEmpty } from "lodash";
import type { PopperProps } from "react-popper";
import { Header } from "./Header";
import { ColorPicker } from "./Picker";
import { PopperWrapper } from "./PopperWrapper";
import { ThemePicker } from "./ThemePicker";
import { PickerProvider } from "./pickerContext";

interface ColorPickerV2Props {
  cssVariables?: Record<string, string>;
  customColors?: Record<string, string>;
  initialColor?: string;
  onAddCustomColor?: (color: string) => void;
  onColorChange?: (color: string) => void;
  popoverButtonClassName?: string;
  popperOptions?: PopperProps<any>["modifiers"];
  shouldRenderThemeColors?: boolean;
  shouldShowAlpha?: boolean;
  themeColors?: Record<string, string>;
}

export const COLOR_PICKER_V2_VIEWS = {
  PICKER: "picker",
  THEME_COLORS: "themeColors",
} as const;

export type ColorPickerV2View =
  (typeof COLOR_PICKER_V2_VIEWS)[keyof typeof COLOR_PICKER_V2_VIEWS];

export const ColorPickerV2 = ({
  initialColor,
  onColorChange,
  popperOptions,
  shouldRenderThemeColors = false,
  shouldShowAlpha = true,
  themeColors,
  customColors,
  cssVariables,
  onAddCustomColor,
  popoverButtonClassName,
}: ColorPickerV2Props) => {
  const initialView = shouldRenderThemeColors
    ? COLOR_PICKER_V2_VIEWS.THEME_COLORS
    : COLOR_PICKER_V2_VIEWS.PICKER;
  const [currentView, setCurrentView] =
    useState<ColorPickerV2View>(initialView);

  const shouldShowThemeColors =
    shouldRenderThemeColors &&
    currentView === COLOR_PICKER_V2_VIEWS.THEME_COLORS &&
    !isEmpty(themeColors);

  const shouldShowPicker = currentView === COLOR_PICKER_V2_VIEWS.PICKER;

  const providerProps = {
    initialColor,
    onColorChange,
    popperOptions,
    themeColors,
    customColors,
    currentView,
    setCurrentView,
    shouldRenderThemeColors,
    onAddCustomColor,
    cssVariables,
  };

  return (
    <PickerProvider {...providerProps}>
      <PopperWrapper popoverButtonClassName={popoverButtonClassName}>
        <Header />
        {shouldShowThemeColors && <ThemePicker />}
        {shouldShowPicker && <ColorPicker shouldShowAlpha={shouldShowAlpha} />}
      </PopperWrapper>
    </PickerProvider>
  );
};
